/* global API, $, dataLayer */

import { Controller } from 'stimulus';
import pubSub from '../helpers/pubSub';

const timeoutPromise = duration => (
  new Promise((resolve) => {
    setTimeout(resolve, duration);
  })
);

function createNewImage(src) {
  const imageElement = document.createElement('img');
  imageElement.className = 'absolute-center';
  imageElement.src = src;
  imageElement.style.opacity = 0;
  imageElement.setAttribute('data-target', 'product.image');
  return imageElement;
}

function onImageLoad(element) {
  return new Promise((resolve) => {
    if (element.complete) {
      resolve(element);
      return;
    }

    element.onload = () => resolve(element);
  });
}

function showImage(element) {
  element.style.opacity = 1;
}

function removeElement(element) {
  element.parentNode.removeChild(element);
}

const waitTick = () => timeoutPromise(10);

class Product extends Controller {
  pickColor({ target }) {
    this.pickTargets.forEach((pickTarget) => {
      pickTarget.classList.remove('is-selected');
    });

    target.classList.add('is-selected');

    this.updateIdOnProduct(target);
    this.updateLabelOnProduct(target);
    this.updateLinksOnProduct(target);
    this.updateImageOnProduct(target);
    this.updatePriceOnProduct(target);
    this.updateOutOfStock(target);
  }

  onCartClick(event) {
    const button = event.currentTarget;
    const id = button.getAttribute('data-variant-id');
    const price = button.getAttribute('data-price');

    this.toggleCartButton(true);

    setTimeout(() => {
      this.toggleCartButton(false);
    }, 2000);

    $.ajax({
      url: API.cartItemStore,
      type: 'POST',
      data: {
        product_variant_id: id,
        quantity: 1,
      },
      complete: () => {
        pubSub.publish('cart:update');
      },
    });

    this.sendToGTM(id, price);
  }

  toggleCartButton(force) {
    this.cartButtonTarget.classList.toggle('btn--secondary', !force);
    this.cartButtonTarget.classList.toggle('btn--success', force);
  }

  updateIdOnProduct(target) {
    this.cartButtonTarget.setAttribute('data-variant-id', target.getAttribute('data-variant-id'));
  }

  updateLabelOnProduct(target) {
    const label = target.getAttribute('data-label');

    this.labelTarget.style.display = label !== '' ? 'block' : 'none';
    this.labelTarget.innerText = label;
  }

  updateLinksOnProduct(target) {
    const url = target.getAttribute('data-url');

    this.linkTargets.forEach((linkTarget) => {
      linkTarget.setAttribute('href', url);
    });
  }

  updatePriceOnProduct(target) {
    const price = target.getAttribute('data-price');
    const originalPrice = target.getAttribute('data-original-price');

    // Check if we should show the 'old' price (before discount)
    this.originalPriceTarget.style.display = originalPrice === '0' ? 'none' : 'inline-block';

    // Output prices
    this.priceTarget.textContent = price;
    this.originalPriceTarget.textContent = originalPrice;
  }

  updateImageOnProduct(target) {
    const imageSource = target.getAttribute('data-image');

    // Create new image element
    const imageElement = createNewImage(imageSource);

    // Get the last inserted image. In case the user hovers multiple dots in quick succession,
    // we want to stack the last hovered item on top
    const lastImageTarget = this.imageTargets[this.imageTargets.length - 1];

    // Append the new image after the last image in the DOM
    lastImageTarget.parentNode.insertBefore(imageElement, lastImageTarget.nextSibling);

    onImageLoad(imageElement)
      .then(waitTick)
      .then(() => showImage(imageElement))
      .then(() => timeoutPromise(150))
      .then(() => removeElement(lastImageTarget));
  }

  updateOutOfStock(target) {
    const maximumQuantity = parseInt(target.getAttribute('data-maximum-quantity'), 10);

    this.cartButtonTarget[maximumQuantity === 0 ? 'setAttribute' : 'removeAttribute']('hidden', 'hidden');
  }

  // eslint-disable-next-line
  sendToGTM(id, price) {
    dataLayer.push({
      event: 'add_to_cart',
      conversion_value: price || 0,
      product_id: id,
    });
  }
}

Product.targets = [
  'image',
  'link',
  'pick',
  'price',
  'originalPrice',
  'label',
  'cartButton',
];

export default Product;
